import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Plug2, CheckCircle2, XCircle, Sparkles, Search, Grid3X3,
  List, Zap, AlertCircle, ExternalLink, Info
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function Top10IntegrationsPage() {
  const [catalog, setCatalog] = useState([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [connectForm, setConnectForm] = useState({
    use_demo: true,
    credentials: {}
  });

  useEffect(() => {
    fetchCatalog();
    fetchConnectedIntegrations();
  }, []);

  const fetchCatalog = async () => {
    try {
      const response = await axios.get(`${API}/integrations/top10/catalog`);
      setCatalog(response.data.integrations);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      toast.error("Failed to load integrations catalog");
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations/top10/user`, {
        withCredentials: true
      });
      setConnectedIntegrations(response.data.integrations);
    } catch (error) {
      console.error('Error fetching connected integrations:', error);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.post(`${API}/integrations/top10/connect`, {
        integration_type: selectedIntegration.type,
        credentials: connectForm.use_demo ? {} : connectForm.credentials,
        use_demo: connectForm.use_demo
      }, { withCredentials: true });

      toast.success(response.data.message);
      setShowConnectModal(false);
      fetchConnectedIntegrations();
      setConnectForm({ use_demo: true, credentials: {} });
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error.response?.data?.detail || "Failed to connect integration");
    }
  };

  const handleDisconnect = async (integrationId) => {
    try {
      await axios.delete(`${API}/integrations/top10/${integrationId}`, {
        withCredentials: true
      });
      toast.success("Integration disconnected");
      fetchConnectedIntegrations();
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error("Failed to disconnect integration");
    }
  };

  const openConnectModal = (integration) => {
    setSelectedIntegration(integration);
    setShowConnectModal(true);
  };

  const openDetailsModal = (integration) => {
    setSelectedIntegration(integration);
    setShowDetailsModal(true);
  };

  const isConnected = (integrationType) => {
    return connectedIntegrations.some(i => i.integration_type === integrationType);
  };

  const filteredCatalog = catalog.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ["all", ...new Set(catalog.map(i => i.category))];

  const getDifficultyColor = (difficulty) => {
    const colors = {
      "Easy": "bg-green-500/10 text-green-500 border-green-500/30",
      "Medium": "bg-amber-500/10 text-amber-500 border-amber-500/30",
      "Hard": "bg-red-500/10 text-red-500 border-red-500/30"
    };
    return colors[difficulty] || colors.Medium;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="top10-integrations-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              Top Business Integrations
            </h1>
            <p className="text-muted-foreground mt-1">
              Connect with 10 essential business tools - {connectedIntegrations.length} connected
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="capitalize"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Plug2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{catalog.length}</p>
                  <p className="text-xs text-muted-foreground">Available</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{connectedIntegrations.length}</p>
                  <p className="text-xs text-muted-foreground">Connected</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {catalog.reduce((sum, i) => sum + (i.triggers?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Triggers</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {catalog.reduce((sum, i) => sum + (i.actions?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Actions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Integrations Grid/List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCatalog.map((integration) => {
              const connected = isConnected(integration.type);
              
              return (
                <Card
                  key={integration.type}
                  className={`glass-card border-2 transition-all hover:shadow-lg ${
                    connected ? 'border-green-500/30 bg-green-500/5' : 'border-border hover:border-primary/30'
                  }`}
                  data-testid={`integration-${integration.type}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-3xl"
                           style={{ backgroundColor: `${integration.color}15` }}>
                        {integration.icon}
                      </div>
                      {connected && (
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="font-heading text-lg">{integration.name}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">
                      {integration.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-xs">
                        <Badge variant="outline" className={getDifficultyColor(integration.difficulty)}>
                          {integration.difficulty}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {integration.category}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {integration.triggers?.length || 0} triggers
                        </span>
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          {integration.actions?.length || 0} actions
                        </span>
                      </div>

                      <Separator />

                      <div className="flex gap-2">
                        {!connected ? (
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => openConnectModal(integration)}
                            data-testid={`connect-${integration.type}`}
                          >
                            <Plug2 className="w-4 h-4 mr-2" />
                            Connect
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                              const conn = connectedIntegrations.find(i => i.integration_type === integration.type);
                              if (conn) handleDisconnect(conn.integration_id);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Disconnect
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetailsModal(integration)}
                        >
                          <Info className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card border-border">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {filteredCatalog.map((integration) => {
                  const connected = isConnected(integration.type);
                  
                  return (
                    <div
                      key={integration.type}
                      className="p-4 hover:bg-muted/30 transition-colors"
                      data-testid={`integration-${integration.type}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                             style={{ backgroundColor: `${integration.color}15` }}>
                          {integration.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground">{integration.name}</h3>
                            {connected && (
                              <Badge className="bg-green-500/10 text-green-500 border-green-500/30">
                                Connected
                              </Badge>
                            )}
                            <Badge variant="outline" className={getDifficultyColor(integration.difficulty)}>
                              {integration.difficulty}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{integration.description}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>{integration.triggers?.length || 0} triggers</span>
                            <span>•</span>
                            <span>{integration.actions?.length || 0} actions</span>
                            <span>•</span>
                            <span className="capitalize">{integration.category}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!connected ? (
                            <Button
                              size="sm"
                              onClick={() => openConnectModal(integration)}
                              data-testid={`connect-${integration.type}`}
                            >
                              <Plug2 className="w-4 h-4 mr-2" />
                              Connect
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const conn = connectedIntegrations.find(i => i.integration_type === integration.type);
                                if (conn) handleDisconnect(conn.integration_id);
                              }}
                            >
                              Disconnect
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailsModal(integration)}
                          >
                            Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {filteredCatalog.length === 0 && !loading && (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium text-foreground">No integrations found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Connect Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedIntegration && (
                <>
                  <span className="text-2xl">{selectedIntegration.icon}</span>
                  Connect {selectedIntegration.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Choose how to connect this integration
            </DialogDescription>
          </DialogHeader>

          {selectedIntegration && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                      Demo Mode Available
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                      Test this integration with demo credentials before connecting your real account
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="use_demo"
                    name="auth_mode"
                    checked={connectForm.use_demo}
                    onChange={() => setConnectForm({ ...connectForm, use_demo: true })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="use_demo" className="cursor-pointer">
                    Use Demo Credentials (Test Mode)
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="use_real"
                    name="auth_mode"
                    checked={!connectForm.use_demo}
                    onChange={() => setConnectForm({ ...connectForm, use_demo: false })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="use_real" className="cursor-pointer">
                    Use My Credentials (Production)
                  </Label>
                </div>
              </div>

              {!connectForm.use_demo && (
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {selectedIntegration.auth_type === 'oauth2' ? 'OAuth2 Setup' : 'API Key'}
                  </p>
                  
                  {selectedIntegration.auth_type === 'oauth2' ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        This integration uses OAuth2. You'll be redirected to authorize access.
                      </p>
                      <a
                        href={selectedIntegration.oauth_config?.authorize_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        Learn how to set up OAuth2
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="api_key">API Key</Label>
                      <Input
                        id="api_key"
                        type="password"
                        placeholder="Enter your API key..."
                        onChange={(e) => setConnectForm({
                          ...connectForm,
                          credentials: { api_key: e.target.value }
                        })}
                        data-testid="api-key-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnect} data-testid="connect-submit">
              {connectForm.use_demo ? 'Connect with Demo' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedIntegration && (
                <>
                  <span className="text-2xl">{selectedIntegration.icon}</span>
                  {selectedIntegration.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedIntegration?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedIntegration && (
            <Tabs defaultValue="triggers" className="flex-1 overflow-hidden">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="triggers">
                  Triggers ({selectedIntegration.triggers?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="actions">
                  Actions ({selectedIntegration.actions?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="triggers" className="mt-4">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {selectedIntegration.triggers?.map((trigger, index) => (
                      <Card key={index} className="p-3">
                        <h4 className="font-semibold text-sm text-foreground mb-1">
                          {trigger.name}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {trigger.description}
                        </p>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="actions" className="mt-4">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {selectedIntegration.actions?.map((action, index) => (
                      <Card key={index} className="p-3">
                        <h4 className="font-semibold text-sm text-foreground mb-1">
                          {action.name}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {action.description}
                        </p>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
